import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export class ResourceDetailsService {
  private static instance: ResourceDetailsService;

  static getInstance(): ResourceDetailsService {
    if (!ResourceDetailsService.instance) {
      ResourceDetailsService.instance = new ResourceDetailsService();
    }
    return ResourceDetailsService.instance;
  }

  private async apiRequest<T>(endpoint: string): Promise<T> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const url = `${API_BASE_URL}/api/infrastructure/aws${endpoint}`;
      
      console.log(`Resource Details API Request: ${url}`);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000,
        withCredentials: false
      });
      
      console.log(`Resource Details API Success for ${endpoint}:`, response.status);
      return response.data;
    } catch (error: any) {
      console.error(`Resource Details API Error for ${endpoint}:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch resource details');
    }
  }

  async getEC2Details(accountId?: string, region?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<any>(`/ec2/instances?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }

  async getRDSDetails(accountId?: string, region?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<any>(`/rds/instances?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }

  async getS3Details(accountId?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);

    const response = await this.apiRequest<any>(`/s3/buckets?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }

  async getLambdaDetails(accountId?: string, region?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<any>(`/lambda/functions?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }

  async getEKSDetails(accountId?: string, region?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<any>(`/eks/clusters?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }

  async getResourceDetails(resourceType: string, accountId?: string, region?: string): Promise<any[]> {
    switch (resourceType) {
      case 'ec2':
        return this.getEC2Details(accountId, region);
      case 'rds':
        return this.getRDSDetails(accountId, region);
      case 's3':
        return this.getS3Details(accountId);
      case 'lambda':
        return this.getLambdaDetails(accountId, region);
      case 'eks':
        return this.getEKSDetails(accountId, region);
      default:
        throw new Error(`Unsupported resource type: ${resourceType}`);
    }
  }
}

export const resourceDetailsService = ResourceDetailsService.getInstance();